import React, { useEffect, useState, useCallback } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import FormInput from '../../components/forms/FormInput';
import FormTextarea from '../../components/forms/FormTextarea';
import FormSelect from '../../components/forms/FormSelect';
import StatusBadge from '../../components/ui/StatusBadge';
import {
  getAllFacilities, createFacility, updateFacility, deleteFacility, toggleFacilityActive,
  getAllBookings, adminCancelBooking, approveBooking, blockSlot, unblockSlot, getBlockedSlots,
  getFacilityStats,
} from '../../api/bookings';

const TABS = {
  ALL_BOOKINGS: 'all_bookings',
  MANAGE: 'manage',
  REPORTS: 'reports',
};

function formatTime(time) {
  const [h, m] = time.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const display = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${display}:${m || '00'} ${ampm}`;
}

function slugify(text) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

const EMPTY_FACILITY_FORM = {
  name: '', slug: '', description: '', operating_start_time: '06:00', operating_end_time: '22:00',
  slot_duration_minutes: 60, max_advance_days: 7, is_active: true, capacity: '',
  rules_text: '', amenities: '', max_bookings_per_user_per_day: 1, min_cancellation_hours: 2,
  requires_approval: false, sort_order: 0,
};

export default function BookingsPage() {
  const [activeTab, setActiveTab] = useState(TABS.ALL_BOOKINGS);

  // Admin state
  const [adminFacilities, setAdminFacilities] = useState([]);
  const [facilityModal, setFacilityModal] = useState({ open: false, facility: null });
  const [facilityForm, setFacilityForm] = useState(EMPTY_FACILITY_FORM);
  const [facilityFormError, setFacilityFormError] = useState('');
  const [facilitySubmitting, setFacilitySubmitting] = useState(false);
  const [allBookings, setAllBookings] = useState([]);
  const [bookingFilters, setBookingFilters] = useState({ facility_id: '', status: '', date: '' });
  const [bookingsPagination, setBookingsPagination] = useState({ current_page: 1, last_page: 1 });
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [blockModal, setBlockModal] = useState({ open: false, facilityId: null });
  const [blockForm, setBlockForm] = useState({ blocked_date: '', start_time: '', end_time: '', reason: '' });
  const [blockedSlotsModal, setBlockedSlotsModal] = useState({ open: false, facilityId: null, slots: [] });

  // Data fetchers
  const fetchAdminFacilities = useCallback(async () => {
    try {
      const res = await getAllFacilities();
      setAdminFacilities(res.data || res);
    } catch (err) {
      console.error('Failed to load admin facilities', err);
    }
  }, []);

  const fetchAllBookings = useCallback(async (page = 1) => {
    setBookingsLoading(true);
    try {
      const params = { ...bookingFilters, page };
      Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
      const res = await getAllBookings(params);
      setAllBookings(res.data || []);
      if (res.meta) {
        setBookingsPagination({ current_page: res.meta.current_page, last_page: res.meta.last_page });
      }
    } catch (err) {
      console.error('Failed to load all bookings', err);
    } finally {
      setBookingsLoading(false);
    }
  }, [bookingFilters]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await getFacilityStats();
      setStats(res.data || res);
    } catch (err) {
      console.error('Failed to load stats', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === TABS.MANAGE) fetchAdminFacilities();
    if (activeTab === TABS.ALL_BOOKINGS) {
      fetchAllBookings();
      fetchAdminFacilities();
    }
    if (activeTab === TABS.REPORTS) fetchStats();
  }, [activeTab, fetchAdminFacilities, fetchAllBookings, fetchStats]);

  // Admin handlers
  const openFacilityModal = (facility = null) => {
    if (facility) {
      setFacilityForm({
        name: facility.name, slug: facility.slug, description: facility.description || '',
        operating_start_time: facility.operating_start_time?.substring(0, 5) || '06:00',
        operating_end_time: facility.operating_end_time?.substring(0, 5) || '22:00',
        slot_duration_minutes: facility.slot_duration_minutes || 60,
        max_advance_days: facility.max_advance_days || 7,
        is_active: facility.is_active ?? true,
        capacity: facility.capacity || '',
        rules_text: facility.rules_text || '',
        amenities: (facility.amenities || []).join(', '),
        max_bookings_per_user_per_day: facility.max_bookings_per_user_per_day || 1,
        min_cancellation_hours: facility.min_cancellation_hours || 2,
        requires_approval: facility.requires_approval || false,
        sort_order: facility.sort_order || 0,
      });
    } else {
      setFacilityForm(EMPTY_FACILITY_FORM);
    }
    setFacilityFormError('');
    setFacilityModal({ open: true, facility });
  };

  const handleFacilitySubmit = async () => {
    setFacilitySubmitting(true);
    setFacilityFormError('');
    try {
      const payload = {
        ...facilityForm,
        amenities: facilityForm.amenities ? facilityForm.amenities.split(',').map(s => s.trim()).filter(Boolean) : [],
        capacity: facilityForm.capacity || null,
        slug: facilityForm.slug || slugify(facilityForm.name),
      };
      if (facilityModal.facility) {
        await updateFacility(facilityModal.facility.id, payload);
      } else {
        await createFacility(payload);
      }
      setFacilityModal({ open: false, facility: null });
      fetchAdminFacilities();
    } catch (err) {
      const errors = err.response?.data?.errors;
      const msg = errors ? Object.values(errors).flat().join(' ') : err.response?.data?.message || 'Failed to save facility.';
      setFacilityFormError(msg);
    } finally {
      setFacilitySubmitting(false);
    }
  };

  const handleDeleteFacility = async (id) => {
    if (!window.confirm('Are you sure you want to delete this facility?')) return;
    try {
      await deleteFacility(id);
      fetchAdminFacilities();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete facility.');
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await toggleFacilityActive(id);
      fetchAdminFacilities();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to toggle facility status.');
    }
  };

  const handleAdminCancelBooking = async (id) => {
    if (!window.confirm('Cancel this booking?')) return;
    try {
      await adminCancelBooking(id);
      fetchAllBookings(bookingsPagination.current_page);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to cancel booking.');
    }
  };

  const handleApproveBooking = async (id) => {
    try {
      await approveBooking(id);
      fetchAllBookings(bookingsPagination.current_page);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to approve booking.');
    }
  };

  const handleBlockSlot = async () => {
    setFacilitySubmitting(true);
    try {
      await blockSlot(blockModal.facilityId, blockForm);
      setBlockModal({ open: false, facilityId: null });
      setBlockForm({ blocked_date: '', start_time: '', end_time: '', reason: '' });
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to block slot.');
    } finally {
      setFacilitySubmitting(false);
    }
  };

  const handleViewBlockedSlots = async (facilityId) => {
    try {
      const res = await getBlockedSlots(facilityId);
      setBlockedSlotsModal({ open: true, facilityId, slots: res.data || [] });
    } catch (err) {
      alert('Failed to load blocked slots.');
    }
  };

  const handleUnblockSlot = async (id) => {
    try {
      await unblockSlot(id);
      handleViewBlockedSlots(blockedSlotsModal.facilityId);
    } catch (err) {
      alert('Failed to unblock slot.');
    }
  };

  const tabs = [
    { key: TABS.ALL_BOOKINGS, label: 'All Bookings' },
    { key: TABS.MANAGE, label: 'Manage Facilities' },
    { key: TABS.REPORTS, label: 'Reports' },
  ];

  return (
    <AppLayout title="Facility Booking" breadcrumbs="Bookings">
      {/* Top Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.key
                ? 'bg-primary-700 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ===== MANAGE FACILITIES TAB ===== */}
      {activeTab === TABS.MANAGE && (
        <>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">All Facilities</h3>
            <Button onClick={() => openFacilityModal()}>Add Facility</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {adminFacilities.map((f) => (
              <div key={f.id} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-900">{f.name}</h4>
                  <StatusBadge status={f.is_active ? 'active' : 'terminated'} />
                </div>
                <div className="text-sm text-gray-500 space-y-1 mb-3">
                  <p>{formatTime(f.operating_start_time)} - {formatTime(f.operating_end_time)}</p>
                  <p>{f.slot_duration_minutes} min slots</p>
                  {f.bookings_count !== undefined && <p>{f.bookings_count} total bookings</p>}
                  {f.requires_approval && <p className="text-yellow-600">Requires approval</p>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="secondary" onClick={() => openFacilityModal(f)}>Edit</Button>
                  <Button size="sm" variant="secondary" onClick={() => handleToggleActive(f.id)}>
                    {f.is_active ? 'Disable' : 'Enable'}
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => { setBlockModal({ open: true, facilityId: f.id }); setBlockForm({ blocked_date: '', start_time: '', end_time: '', reason: '' }); }}>
                    Block Slot
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => handleViewBlockedSlots(f.id)}>
                    Blocked
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => handleDeleteFacility(f.id)}>Delete</Button>
                </div>
              </div>
            ))}
            {adminFacilities.length === 0 && (
              <div className="col-span-full text-center py-8 text-gray-500">No facilities found.</div>
            )}
          </div>
        </>
      )}

      {/* ===== ALL BOOKINGS TAB ===== */}
      {activeTab === TABS.ALL_BOOKINGS && (
        <>
          <div className="flex flex-wrap gap-3 mb-4">
            <FormSelect
              label="Facility"
              className="w-48"
              value={bookingFilters.facility_id}
              onChange={(e) => setBookingFilters(prev => ({ ...prev, facility_id: e.target.value }))}
              options={adminFacilities.map(f => ({ value: f.id, label: f.name }))}
            />
            <FormSelect
              label="Status"
              className="w-40"
              value={bookingFilters.status}
              onChange={(e) => setBookingFilters(prev => ({ ...prev, status: e.target.value }))}
              options={[
                { value: 'confirmed', label: 'Confirmed' },
                { value: 'pending', label: 'Pending' },
                { value: 'cancelled', label: 'Cancelled' },
              ]}
            />
            <FormInput
              label="Date"
              type="date"
              className="w-44"
              value={bookingFilters.date}
              onChange={(e) => setBookingFilters(prev => ({ ...prev, date: e.target.value }))}
            />
            <div className="flex items-end">
              <Button variant="secondary" onClick={() => { setBookingFilters({ facility_id: '', status: '', date: '' }); }}>
                Clear
              </Button>
            </div>
          </div>

          {bookingsLoading ? (
            <div className="text-center py-8 text-gray-500">Loading bookings...</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-gray-500">
                      <th className="pb-2 pr-4 font-medium">Facility</th>
                      <th className="pb-2 pr-4 font-medium">Apartment</th>
                      <th className="pb-2 pr-4 font-medium">Date</th>
                      <th className="pb-2 pr-4 font-medium">Time</th>
                      <th className="pb-2 pr-4 font-medium">Status</th>
                      <th className="pb-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allBookings.map((b) => (
                      <tr key={b.id} className="border-b border-gray-100">
                        <td className="py-3 pr-4">{b.facility?.name}</td>
                        <td className="py-3 pr-4">{b.apartment?.flat_number || '-'}</td>
                        <td className="py-3 pr-4">{new Date(b.booking_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                        <td className="py-3 pr-4">{formatTime(b.start_time)} - {formatTime(b.end_time)}</td>
                        <td className="py-3 pr-4"><StatusBadge status={b.status} /></td>
                        <td className="py-3">
                          <div className="flex gap-2">
                            {b.status === 'pending' && (
                              <Button size="sm" onClick={() => handleApproveBooking(b.id)}>Approve</Button>
                            )}
                            {(b.status === 'confirmed' || b.status === 'pending') && (
                              <Button size="sm" variant="danger" onClick={() => handleAdminCancelBooking(b.id)}>Cancel</Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {allBookings.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-8 text-gray-500">No bookings found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {bookingsPagination.last_page > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  {Array.from({ length: bookingsPagination.last_page }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => fetchAllBookings(page)}
                      className={`px-3 py-1 rounded text-sm ${
                        page === bookingsPagination.current_page
                          ? 'bg-primary-700 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ===== REPORTS TAB ===== */}
      {activeTab === TABS.REPORTS && (
        <>
          {statsLoading ? (
            <div className="text-center py-8 text-gray-500">Loading stats...</div>
          ) : stats ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="text-2xl font-bold text-gray-900">{stats.total_bookings_today}</div>
                  <div className="text-sm text-gray-500">Bookings Today</div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="text-2xl font-bold text-gray-900">{stats.active_facilities}</div>
                  <div className="text-sm text-gray-500">Active Facilities</div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="text-2xl font-bold text-yellow-600">{stats.pending_approvals}</div>
                  <div className="text-sm text-gray-500">Pending Approvals</div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="text-2xl font-bold text-primary-700">{stats.utilization_percent}%</div>
                  <div className="text-sm text-gray-500">Today&apos;s Utilization</div>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">Bookings by Facility (Last 30 Days)</h3>
              <div className="space-y-3">
                {stats.by_facility?.map((f) => (
                  <div key={f.id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-gray-900">{f.name}</span>
                      {!f.is_active && <StatusBadge status="terminated" />}
                    </div>
                    <span className="text-lg font-semibold text-gray-700">{f.bookings_count}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">No stats available.</div>
          )}
        </>
      )}

      {/* ===== ADD/EDIT FACILITY MODAL ===== */}
      <Modal
        isOpen={facilityModal.open}
        onClose={() => setFacilityModal({ open: false, facility: null })}
        title={facilityModal.facility ? 'Edit Facility' : 'Add Facility'}
        size="lg"
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FormInput
              label="Name"
              value={facilityForm.name}
              onChange={(e) => setFacilityForm(prev => ({ ...prev, name: e.target.value, slug: prev.slug || slugify(e.target.value) }))}
            />
            <FormInput
              label="Slug"
              value={facilityForm.slug}
              onChange={(e) => setFacilityForm(prev => ({ ...prev, slug: e.target.value }))}
            />
          </div>
          <FormTextarea
            label="Description"
            value={facilityForm.description}
            onChange={(e) => setFacilityForm(prev => ({ ...prev, description: e.target.value }))}
            rows={2}
          />
          <div className="grid grid-cols-2 gap-3">
            <FormInput label="Operating Start" type="time" value={facilityForm.operating_start_time} onChange={(e) => setFacilityForm(prev => ({ ...prev, operating_start_time: e.target.value }))} />
            <FormInput label="Operating End" type="time" value={facilityForm.operating_end_time} onChange={(e) => setFacilityForm(prev => ({ ...prev, operating_end_time: e.target.value }))} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <FormInput label="Slot Duration (min)" type="number" value={facilityForm.slot_duration_minutes} onChange={(e) => setFacilityForm(prev => ({ ...prev, slot_duration_minutes: parseInt(e.target.value) || '' }))} />
            <FormInput label="Max Advance Days" type="number" value={facilityForm.max_advance_days} onChange={(e) => setFacilityForm(prev => ({ ...prev, max_advance_days: parseInt(e.target.value) || '' }))} />
            <FormInput label="Capacity" type="number" placeholder="Optional" value={facilityForm.capacity} onChange={(e) => setFacilityForm(prev => ({ ...prev, capacity: e.target.value }))} />
          </div>
          <FormTextarea
            label="Rules & Regulations"
            value={facilityForm.rules_text}
            onChange={(e) => setFacilityForm(prev => ({ ...prev, rules_text: e.target.value }))}
            rows={2}
            placeholder="Enter rules that will be displayed to users..."
          />
          <FormInput
            label="Amenities (comma separated)"
            value={facilityForm.amenities}
            onChange={(e) => setFacilityForm(prev => ({ ...prev, amenities: e.target.value }))}
            placeholder="Changing Rooms, Lights, Seating"
          />
          <div className="grid grid-cols-3 gap-3">
            <FormInput label="Max Bookings/User/Day" type="number" value={facilityForm.max_bookings_per_user_per_day} onChange={(e) => setFacilityForm(prev => ({ ...prev, max_bookings_per_user_per_day: parseInt(e.target.value) || 1 }))} />
            <FormInput label="Min Cancel Hours" type="number" value={facilityForm.min_cancellation_hours} onChange={(e) => setFacilityForm(prev => ({ ...prev, min_cancellation_hours: parseInt(e.target.value) || 0 }))} />
            <FormInput label="Sort Order" type="number" value={facilityForm.sort_order} onChange={(e) => setFacilityForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))} />
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={facilityForm.requires_approval} onChange={(e) => setFacilityForm(prev => ({ ...prev, requires_approval: e.target.checked }))} className="rounded" />
              Requires Approval
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={facilityForm.is_active} onChange={(e) => setFacilityForm(prev => ({ ...prev, is_active: e.target.checked }))} className="rounded" />
              Active
            </label>
          </div>

          {facilityFormError && <p className="text-red-500 text-sm">{facilityFormError}</p>}

          <div className="flex gap-3 justify-end pt-2">
            <Button variant="secondary" onClick={() => setFacilityModal({ open: false, facility: null })}>Cancel</Button>
            <Button loading={facilitySubmitting} onClick={handleFacilitySubmit}>
              {facilityModal.facility ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ===== BLOCK SLOT MODAL ===== */}
      <Modal
        isOpen={blockModal.open}
        onClose={() => setBlockModal({ open: false, facilityId: null })}
        title="Block Time Slot"
      >
        <div className="space-y-3">
          <FormInput label="Date" type="date" value={blockForm.blocked_date} onChange={(e) => setBlockForm(prev => ({ ...prev, blocked_date: e.target.value }))} />
          <div className="grid grid-cols-2 gap-3">
            <FormInput label="Start Time (optional)" type="time" value={blockForm.start_time} onChange={(e) => setBlockForm(prev => ({ ...prev, start_time: e.target.value }))} />
            <FormInput label="End Time (optional)" type="time" value={blockForm.end_time} onChange={(e) => setBlockForm(prev => ({ ...prev, end_time: e.target.value }))} />
          </div>
          <p className="text-xs text-gray-500">Leave times empty to block the entire day.</p>
          <FormInput label="Reason (optional)" value={blockForm.reason} onChange={(e) => setBlockForm(prev => ({ ...prev, reason: e.target.value }))} />
          <div className="flex gap-3 justify-end pt-2">
            <Button variant="secondary" onClick={() => setBlockModal({ open: false, facilityId: null })}>Cancel</Button>
            <Button loading={facilitySubmitting} onClick={handleBlockSlot}>Block Slot</Button>
          </div>
        </div>
      </Modal>

      {/* ===== BLOCKED SLOTS LIST MODAL ===== */}
      <Modal
        isOpen={blockedSlotsModal.open}
        onClose={() => setBlockedSlotsModal({ open: false, facilityId: null, slots: [] })}
        title="Blocked Slots"
      >
        <div className="space-y-2">
          {blockedSlotsModal.slots.length === 0 ? (
            <p className="text-center py-4 text-gray-500">No blocked slots.</p>
          ) : (
            blockedSlotsModal.slots.map((s) => (
              <div key={s.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                <div>
                  <div className="text-sm font-medium">
                    {new Date(s.blocked_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    {s.start_time ? ` ${formatTime(s.start_time)} - ${formatTime(s.end_time)}` : ' (All Day)'}
                  </div>
                  {s.reason && <div className="text-xs text-gray-500">{s.reason}</div>}
                  {s.creator && <div className="text-xs text-gray-400">By {s.creator.name}</div>}
                </div>
                <Button size="sm" variant="danger" onClick={() => handleUnblockSlot(s.id)}>Remove</Button>
              </div>
            ))
          )}
        </div>
      </Modal>
    </AppLayout>
  );
}
